package br.com.heyagenda.domain.util

data class CurrentDateTime(
    val year: Int,
    val monthNumber: Int,
    val dayOfMonth: Int,
    val hour: Int,
    val minute: Int,
    val timeZoneId: String = "America/Sao_Paulo",
    val utcOffsetMinutes: Int = -180
) {
    val totalMinutes: Int get() = hour * 60 + minute
    val dateIso: String get() = "$year-${monthNumber.toString().padStart(2, '0')}-${dayOfMonth.toString().padStart(2, '0')}"
}

expect fun getPlatformCurrentDateTime(): CurrentDateTime

object DateTimeZoneUtils {
    /**
     * Converts a local date and time ("2026-09-09", "10:00") into a UTC ISO-8601 string ("2026-09-09T13:00:00Z").
     * @param offsetMinutes Standard UTC offset in minutes (e.g. -180 for UTC-3, +60 for UTC+1).
     */
    fun localToUtcIso(dateIso: String, time: String, offsetMinutes: Int): String {
        return try {
            val dateParts = dateIso.trim().split("-")
            var y = dateParts[0].toInt()
            var m = dateParts[1].toInt()
            var d = dateParts[2].toInt()

            val timeParts = time.trim().split(":")
            val h = timeParts[0].toInt()
            val min = if (timeParts.size > 1) timeParts[1].toInt() else 0

            val localMinutes = h * 60 + min
            var utcMinutes = localMinutes - offsetMinutes

            while (utcMinutes < 0) {
                utcMinutes += 24 * 60
                d--
                if (d < 1) {
                    m--
                    if (m < 1) {
                        m = 12
                        y--
                    }
                    d = daysInMonth(y, m)
                }
            }

            while (utcMinutes >= 24 * 60) {
                utcMinutes -= 24 * 60
                d++
                if (d > daysInMonth(y, m)) {
                    d = 1
                    m++
                    if (m > 12) {
                        m = 1
                        y++
                    }
                }
            }

            val utcH = (utcMinutes / 60).toString().padStart(2, '0')
            val utcM = (utcMinutes % 60).toString().padStart(2, '0')
            val yStr = y.toString()
            val mStr = m.toString().padStart(2, '0')
            val dStr = d.toString().padStart(2, '0')
            "${yStr}-${mStr}-${dStr}T${utcH}:${utcM}:00Z"
        } catch (_: Throwable) {
            "${dateIso}T${time}:00Z"
        }
    }

    /**
     * Converts a UTC ISO-8601 string ("2026-09-09T13:00:00Z") to a local date and time Pair("2026-09-09", "10:00").
     * @param targetOffsetMinutes Target local UTC offset in minutes (e.g. -180 for UTC-3).
     */
    fun utcIsoToLocal(utcIso: String, targetOffsetMinutes: Int): Pair<String, String> {
        return try {
            val clean = utcIso.trim().replace("Z", "")
            val dtParts = clean.split("T")
            val dateParts = dtParts[0].split("-")
            var y = dateParts[0].toInt()
            var m = dateParts[1].toInt()
            var d = dateParts[2].toInt()

            val timeParts = dtParts[1].split(":")
            val utcH = timeParts[0].toInt()
            val utcMin = timeParts[1].toInt()

            var localMinutes = (utcH * 60 + utcMin) + targetOffsetMinutes

            while (localMinutes < 0) {
                localMinutes += 24 * 60
                d--
                if (d < 1) {
                    m--
                    if (m < 1) {
                        m = 12
                        y--
                    }
                    d = daysInMonth(y, m)
                }
            }

            while (localMinutes >= 24 * 60) {
                localMinutes -= 24 * 60
                d++
                if (d > daysInMonth(y, m)) {
                    d = 1
                    m++
                    if (m > 12) {
                        m = 1
                        y++
                    }
                }
            }

            val localH = (localMinutes / 60).toString().padStart(2, '0')
            val localM = (localMinutes % 60).toString().padStart(2, '0')
            val yStr = y.toString()
            val mStr = m.toString().padStart(2, '0')
            val dStr = d.toString().padStart(2, '0')

            Pair("${yStr}-${mStr}-${dStr}", "${localH}:${localM}")
        } catch (_: Throwable) {
            val parts = utcIso.split("T")
            val date = parts.getOrNull(0) ?: ""
            val time = parts.getOrNull(1)?.take(5) ?: ""
            Pair(date, time)
        }
    }

    private fun daysInMonth(year: Int, month: Int): Int {
        return when (month) {
            1, 3, 5, 7, 8, 10, 12 -> 31
            4, 6, 9, 11 -> 30
            2 -> if ((year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)) 29 else 28
            else -> 30
        }
    }
}
